// Import order is manually controlled in this file to ensure CSS loads after Vuetify
import { VueQueryPlugin } from '@tanstack/vue-query';

import { queryClient } from '@/config/vueQuery';
import { createPinia } from 'pinia';
import { createApp } from 'vue';
import './layers.css';
/* TOASTERS */
import Toast from 'vue-toastification';

import { login } from '@/api/calls/auth/auth.api';
import { launchCrowdinInContext } from '@/api/clients/crowdin/Crowdin';
import { fetchEnv } from '@/config/api';
import { setupBeamerScript } from '@/config/beamerConfig';
import Bugsnag from '@/config/bugsnag';
import { initI18N } from '@/config/i18n';
import setIcons from '@/config/icons';
import { ToastOptions } from '@/config/vue-toastification';
import vuetify from '@/config/vuetify';
import { getSpacesRole, roleSpaces } from '@/helpers/spaces';
import { isFreemiumOrTrial } from '@/helpers/users';
import { assetsPlugin, i18nPlugin, reporterPlugin } from '@/plugins';
import { useAuthStore } from '@/stores/auth';
import { useGlobalStore } from '@/stores/global';
import { useLocaleStore } from '@/stores/locales';

// CSS imports - MUST be after vuetify import to ensure proper style priority
import '@af-lib/ui-lib/dist/ui-lib.css';
import '@af-lib/vue-ui/vue-ui.css';
import '@/styles/main.scss';
import '@/tailwind.css';

import App from './App.vue';
import { STATIC_DATA_PATHS } from './enums/constants';
import { router } from './router';

// Create our app
const app = createApp(App);

// Setup Fontawesome library
setIcons(app);

// setup vuetify
app.use(vuetify);

// VueQuery
app.use(VueQueryPlugin, {
  queryClient,
  enableDevtoolsV6Plugin: true
});

// setup stores
app.use(createPinia());

// Global and auth stores
const globalStore = useGlobalStore();
const authStore = useAuthStore();
const localeStore = useLocaleStore();

// Fetch the config.json file
fetchEnv().then(
  ({
    environment,
    cache,
    bucket_s3,
    viktor_url,
    patrick_url,
    static_assets_base_url,
    dynamic_assets_base_url,
    redirect_logout,
    domain_cookies,
    map_box_token,
    gaston_url,
    admin_url,
    assessment_url,
    auth_url,
    personal_account_url,
    report_manager_url,
    root_url,
    mixpanel_proxy,
    set_auto_cookie_spaces,
    pusher,
    users,
    reverb_server_url,
    static_data_url,
    jeudimerci_programme_id,
    ...userData
  }) => {
    globalStore.setEnvironment(environment);
    globalStore.setCache(cache);
    globalStore.setTranslationURL(bucket_s3);
    globalStore.setAssetsURL(dynamic_assets_base_url);
    globalStore.setStaticAssetsURL(static_assets_base_url);
    globalStore.setLogoutURL(redirect_logout);
    globalStore.setDomainCookies(domain_cookies);
    globalStore.setAPIURL(viktor_url);
    globalStore.setPatrickURL(patrick_url);
    globalStore.setMapBoxToken(map_box_token);
    globalStore.setGastonUrl(gaston_url);
    globalStore.setAdminUrl(admin_url);
    globalStore.setAssessmentURL(assessment_url);
    globalStore.setAuthUrl(auth_url);
    globalStore.setPersonalAccountUrl(personal_account_url);
    globalStore.setReportManagerUrl(report_manager_url);
    globalStore.setRootUrl(root_url);
    globalStore.setPusher(pusher);
    globalStore.setMixpanelProxy(mixpanel_proxy);
    globalStore.setReverbUrl(reverb_server_url);
    globalStore.setAutoCookieSpaces(set_auto_cookie_spaces);
    globalStore.setStaticDataUrl(static_data_url);
    globalStore.setJeuDiMerciProgrammeId(jeudimerci_programme_id);

    if (environment === 'dev' && users) {
      globalStore.setDevUsers(users);
    }

    app.use(assetsPlugin);
    app.use(i18nPlugin);
    // Mount app
    app.mount('#app');

    login(userData).then(async () => {
      // Set "hook"
      authStore.setLoginDone();

      const globalStore = useGlobalStore();

      await authStore.loadMe().then(async () => {
        // Set "hook"
        authStore.setLoadMeDone();

        const shouldRedirectToManager =
          authStore.hasPermission('manager_r') &&
          (!authStore.hasPermission('recruiter_r') || roleSpaces.value === 'manager');

        if (shouldRedirectToManager) {
          const path = window.location.pathname;
          const match = path.match(/profile\/([a-z0-9]{32})(?:\/([^/]+))?(?:\/([^/]+))?/);
          if (match) {
            const uuid = match[1];
            const subPath = match[2];
            const subPathUuid = match[3];
            const target = subPath || 'synthesis';
            const targetUuid = ['predict', 'affinity'].includes(target) ? subPathUuid : undefined;

            return window.location.assign(
              `${useGlobalStore().gaston_url}/manager/profile/${uuid}/${target}${targetUuid ? `/${targetUuid}` : ''}`
            );
          }
        }

        const roleState = getSpacesRole();
        if (roleState === 'noCookies') return window.location.assign(`${useGlobalStore()?.auth_url}/spaces`);
        if (roleState === 'isManager' && globalStore.environment === 'dev')
          return window.location.assign(`${useGlobalStore()?.gaston_url}/manager/shared-candidates`);

        // Bug Tracking
        app.use(Bugsnag);

        const inContextData = launchCrowdinInContext();

        await initI18N(authStore.getLocaleId, inContextData).then(() => {
          /**
           * Init router and reporting here.
           * Why ?
           * That's a good question dude ... If initializing one of them before, the loadMe and loadCompany won't be launched, which will result on bad mixpanel's global data
           */
          // set routing
          app.use(router);
          // set reporter
          app.use(reporterPlugin);

          // Toast messages (moved in function to use pinia)
          app.use(Toast, ToastOptions(localeStore.getIsLocaleRtl));

          // Hide loader, everything is fine
          globalStore.setLoading(false);
        });

        const url = `${useGlobalStore().getStaticDataUrl}${STATIC_DATA_PATHS.personality}`;
        fetch(url).then(async response => {
          globalStore.setPersonalityGradients(await response.json());
        });

        // Initialize beamer for user with subscription on prod
        if (!isFreemiumOrTrial() && globalStore.environment === 'prod') {
          setupBeamerScript();
        }
      });
    });
  }
);
